import React, { useState, useEffect, useRef } from 'react';
import { C } from '../utils/theme.js';
import { t } from '../utils/i18n.js';
import {
  CARD_W, CARD_H, FIELD_DEFS, SAMPLE_DATA, formatField,
  drawDesign, collectImageSrcs, defaultPresets
} from '../utils/qslRender.js';

// Offscreen canvas for measuring text bounding boxes
var measureCanvas = typeof document !== "undefined" ? document.createElement("canvas") : null;

function sampleText(el) {
  if (el.field) {
    var v = formatField(el.field, SAMPLE_DATA);
    if (!v) v = el.placeholder || "";
    return (el.prefix || "") + v + (el.suffix || "");
  }
  return el.text || "";
}

function newId() { return "e" + Date.now() + Math.floor(Math.random() * 1000); }

// Extract the alpha (0..1) from an overlay color like "rgba(8,11,30,0.35)"
function bgOverlayAlpha(overlay) {
  if (!overlay) return 0;
  var m = String(overlay).match(/rgba?\([^)]*,\s*([\d.]+)\s*\)/);
  return m ? parseFloat(m[1]) : 0;
}

export default function QSLDesignStudio(props) {
  var lang = props.lang;
  var authToken = props.authToken;
  var onDesignSaved = props.onDesignSaved;

  var designsSt = useState([]);
  var designs = designsSt[0]; var setDesigns = designsSt[1];

  // The design currently being edited: { id, name, is_default, layout }
  var presets = defaultPresets();
  var curSt = useState({ id: null, name: presets[0].name, is_default: false, layout: JSON.parse(JSON.stringify(presets[0].layout)) });
  var cur = curSt[0]; var setCur = curSt[1];

  var selSt = useState(null);
  var selId = selSt[0]; var setSelId = selSt[1];

  var savingSt = useState(false);
  var saving = savingSt[0]; var setSaving = savingSt[1];
  var msgSt = useState(null);
  var msg = msgSt[0]; var setMsg = msgSt[1];

  var canvasRef = useRef(null);
  var imagesRef = useRef({});
  var imgVerSt = useState(0);
  var imgVer = imgVerSt[0]; var setImgVer = imgVerSt[1];

  var dragRef = useRef(null);

  var layout = cur.layout;
  var elements = layout.elements || [];

  function setLayout(newLayout) {
    setCur(function(c) { return Object.assign({}, c, { layout: newLayout }); });
  }
  function findEl(id) {
    for (var i = 0; i < elements.length; i++) if (elements[i].id === id) return elements[i];
    return null;
  }

  // ── Load saved designs ──
  function loadDesigns() {
    fetch("/api/qsl/designs", { headers: { "Authorization": "Bearer " + authToken } })
      .then(function(r) { return r.json(); })
      .then(function(d) { if (d.designs) setDesigns(d.designs); })
      .catch(function() {});
  }
  useEffect(function() { loadDesigns(); }, [authToken]);

  // ── Preload images referenced by the layout ──
  useEffect(function() {
    var srcs = collectImageSrcs(layout);
    var pending = 0;
    srcs.forEach(function(src) {
      if (imagesRef.current[src]) return;
      pending++;
      var im = new Image();
      im.onload = function() { imagesRef.current[src] = im; setImgVer(function(v) { return v + 1; }); };
      im.onerror = function() {};
      im.src = src;
    });
    if (pending === 0) setImgVer(function(v) { return v + 1; });
  }, [layout]);

  // ── Bounding box of an element (logical coords) ──
  function bbox(el) {
    if (el.type === "rect" || el.type === "image") {
      return { x: el.x, y: el.y, w: el.w, h: el.h };
    }
    if (el.type === "line") {
      var x = Math.min(el.x, el.x2), y = Math.min(el.y, el.y2);
      return { x: x - 4, y: y - 4, w: Math.abs(el.x2 - el.x) + 8, h: Math.abs(el.y2 - el.y) + 8 };
    }
    // text / field
    var mctx = measureCanvas.getContext("2d");
    var weight = el.weight || "normal";
    var style = el.italic ? "italic " : "";
    mctx.font = style + weight + " " + (el.size || 20) + "px " + (el.font || "Arial");
    var txt = sampleText(el) || "Aa";
    var w = mctx.measureText(txt).width;
    var h = (el.size || 20);
    var bx = el.x;
    if ((el.align || "left") === "center") bx = el.x - w / 2;
    else if (el.align === "right") bx = el.x - w;
    var by = el.y - h * 0.82;
    var topPad = el.label ? h * 0.6 : 0;
    return { x: bx - 2, y: by - topPad - 2, w: w + 4, h: h * 1.1 + topPad + 4 };
  }

  // ── Redraw ──
  function redraw() {
    var cv = canvasRef.current;
    if (!cv) return;
    var ctx = cv.getContext("2d");
    drawDesign(ctx, layout, SAMPLE_DATA, imagesRef.current);
    if (selId) {
      var el = findEl(selId);
      if (el) {
        var b = bbox(el);
        ctx.save();
        ctx.strokeStyle = "#3fcfd5";
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 4]);
        ctx.strokeRect(b.x, b.y, b.w, b.h);
        ctx.setLineDash([]);
        ctx.fillStyle = "#3fcfd5";
        ctx.fillRect(b.x + b.w - 5, b.y + b.h - 5, 10, 10);
        ctx.restore();
      }
    }
  }
  useEffect(function() { redraw(); });

  // ── Mouse interaction ──
  function getPos(e) {
    var cv = canvasRef.current;
    var rect = cv.getBoundingClientRect();
    var scale = CARD_W / rect.width;
    return { x: (e.clientX - rect.left) * scale, y: (e.clientY - rect.top) * scale };
  }
  function hitTest(pos) {
    for (var i = elements.length - 1; i >= 0; i--) {
      var b = bbox(elements[i]);
      if (pos.x >= b.x && pos.x <= b.x + b.w && pos.y >= b.y && pos.y <= b.y + b.h) return elements[i];
    }
    return null;
  }
  function onCanvasDown(e) {
    e.preventDefault();
    var pos = getPos(e);
    var el = hitTest(pos);
    if (el) {
      setSelId(el.id);
      dragRef.current = { id: el.id, startX: pos.x, startY: pos.y, elX: el.x, elY: el.y, type: el.type, x2: el.x2, y2: el.y2 };
    } else {
      setSelId(null);
    }
  }
  useEffect(function() {
    function onMove(e) {
      var d = dragRef.current;
      if (!d) return;
      var cv = canvasRef.current; if (!cv) return;
      var rect = cv.getBoundingClientRect();
      var scale = CARD_W / rect.width;
      var px = (e.clientX - rect.left) * scale, py = (e.clientY - rect.top) * scale;
      var dx = px - d.startX, dy = py - d.startY;
      setCur(function(c) {
        var els = c.layout.elements.map(function(el) {
          if (el.id !== d.id) return el;
          var n = Object.assign({}, el, { x: Math.round(d.elX + dx), y: Math.round(d.elY + dy) });
          if (el.type === "line") { n.x2 = Math.round(d.x2 + dx); n.y2 = Math.round(d.y2 + dy); }
          return n;
        });
        return Object.assign({}, c, { layout: Object.assign({}, c.layout, { elements: els }) });
      });
    }
    function onUp() { dragRef.current = null; }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return function() { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, []);

  // ── Element operations ──
  function addElement(type, extra) {
    var base = { id: newId(), x: 120, y: 270, opacity: 1 };
    var el;
    if (type === "text") el = Object.assign(base, { type: "text", text: "New text", size: 28, color: "#eef1ff", weight: "bold", align: "left", font: "Arial" });
    else if (type === "field") el = Object.assign(base, { type: "field", field: extra || "to_callsign", size: 28, color: "#eef1ff", weight: "bold", align: "left", font: "Arial" });
    else if (type === "rect") el = Object.assign(base, { type: "rect", w: 200, h: 100, fill: "rgba(63,207,213,0.1)", stroke: "#34407e", sw: 1, radius: 10 });
    else if (type === "line") el = Object.assign(base, { type: "line", x: 120, y: 300, x2: 380, y2: 300, color: "#3fcfd5", sw: 2 });
    else if (type === "image") el = Object.assign(base, { type: "image", src: extra, w: 160, h: 120, radius: 0 });
    var els = elements.concat([el]);
    setLayout(Object.assign({}, layout, { elements: els }));
    setSelId(el.id);
  }
  function updateEl(id, patch) {
    var els = elements.map(function(el) { return el.id === id ? Object.assign({}, el, patch) : el; });
    setLayout(Object.assign({}, layout, { elements: els }));
  }
  function updateSel(patch) { if (selId) updateEl(selId, patch); }
  function deleteSel() {
    if (!selId) return;
    setLayout(Object.assign({}, layout, { elements: elements.filter(function(el) { return el.id !== selId; }) }));
    setSelId(null);
  }
  function duplicateSel() {
    var el = findEl(selId); if (!el) return;
    var copy = Object.assign({}, el, { id: newId(), x: el.x + 20, y: el.y + 20 });
    setLayout(Object.assign({}, layout, { elements: elements.concat([copy]) }));
    setSelId(copy.id);
  }
  function reorderSel(dir) {
    var idx = elements.findIndex(function(el) { return el.id === selId; });
    if (idx < 0) return;
    var els = elements.slice();
    if (dir === "front") { var f = els.splice(idx, 1)[0]; els.push(f); }
    else if (dir === "back") { var bk = els.splice(idx, 1)[0]; els.unshift(bk); }
    setLayout(Object.assign({}, layout, { elements: els }));
  }

  // ── Image / background upload ──
  function uploadImage(file, cb) {
    var reader = new FileReader();
    reader.onload = function() {
      fetch("/api/qsl/designs/bg", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + authToken },
        body: JSON.stringify({ image: reader.result })
      }).then(function(r) { return r.json(); })
        .then(function(d) { if (d.url) cb(d.url); else setMsg({ ok: false, txt: d.error || "Erro" }); })
        .catch(function() { setMsg({ ok: false, txt: "Erro de upload" }); });
    };
    reader.readAsDataURL(file);
  }
  function onLogoFile(e) {
    var f = e.target.files && e.target.files[0]; if (!f) return;
    uploadImage(f, function(url) { addElement("image", url); });
    e.target.value = "";
  }
  function onBgFile(e) {
    var f = e.target.files && e.target.files[0]; if (!f) return;
    uploadImage(f, function(url) {
      setLayout(Object.assign({}, layout, { bg: { type: "image", src: url, overlay: "rgba(8,11,30,0.35)" } }));
    });
    e.target.value = "";
  }

  // ── Persistence ──
  function makeThumbnail() {
    var off = document.createElement("canvas");
    off.width = 360; off.height = 216;
    var octx = off.getContext("2d");
    octx.scale(360 / CARD_W, 216 / CARD_H);
    drawDesign(octx, layout, SAMPLE_DATA, imagesRef.current);
    return off.toDataURL("image/jpeg", 0.7);
  }
  function saveDesign(asNew) {
    if (saving) return;
    setSaving(true); setMsg(null);
    var thumb;
    try { thumb = makeThumbnail(); } catch (e) {
      console.error("[QSL] Thumbnail error:", e);
      thumb = null;
    }
    var body = { name: cur.name, layout: layout, thumbnail: thumb };
    var url = "/api/qsl/designs", method = "POST";
    if (!asNew && cur.id) { url = "/api/qsl/designs/" + cur.id; method = "PUT"; }
    fetch(url, {
      method: method,
      headers: { "Content-Type": "application/json", "Authorization": "Bearer " + authToken },
      body: JSON.stringify(body)
    }).then(function(r) { return r.json(); })
      .then(function(d) {
        setSaving(false);
        if (d.error) { setMsg({ ok: false, txt: d.error }); return; }
        if (d.id) setCur(function(c) { return Object.assign({}, c, { id: d.id }); });
        setMsg({ ok: true, txt: t(lang, "qslDesignSaved") });
        loadDesigns();
        if (typeof onDesignSaved === "function") onDesignSaved();
      })
      .catch(function() { setSaving(false); setMsg({ ok: false, txt: "Erro ao salvar" }); });
  }
  function loadDesign(d) {
    setCur({ id: d.id, name: d.name, is_default: !!d.is_default, layout: JSON.parse(JSON.stringify(d.layout)) });
    setSelId(null);
    setMsg(null);
  }
  function newFromPreset(p) {
    setCur({ id: null, name: p.name, is_default: false, layout: JSON.parse(JSON.stringify(p.layout)) });
    setSelId(null);
    setMsg(null);
  }
  function setDefault() {
    if (!cur.id) { setMsg({ ok: false, txt: t(lang, "qslDesignSaveFirst") }); return; }
    fetch("/api/qsl/designs/" + cur.id + "/default", { method: "POST", headers: { "Authorization": "Bearer " + authToken } })
      .then(function(r) { return r.json(); })
      .then(function() { setCur(function(c) { return Object.assign({}, c, { is_default: true }); }); loadDesigns(); if (typeof onDesignSaved === "function") onDesignSaved(); setMsg({ ok: true, txt: t(lang, "qslDesignDefaultSet") }); })
      .catch(function() {});
  }
  function deleteDesign() {
    if (!cur.id) return;
    if (!window.confirm(t(lang, "qslDesignDeleteConfirm"))) return;
    fetch("/api/qsl/designs/" + cur.id, { method: "DELETE", headers: { "Authorization": "Bearer " + authToken } })
      .then(function(r) { return r.json(); })
      .then(function() { newFromPreset(presets[0]); loadDesigns(); if (typeof onDesignSaved === "function") onDesignSaved(); })
      .catch(function() {});
  }

  var sel = selId ? findEl(selId) : null;

  // ── Styles ──
  var btn = { background: C.s2, color: C.t1, border: "1px solid " + C.bd, borderRadius: 8, padding: "8px 12px", cursor: "pointer", fontSize: 13, fontWeight: 600 };
  var btnPrimary = Object.assign({}, btn, { background: C.a1, color: "#04223a", border: "none" });
  var lbl = { display: "block", fontSize: 11, color: C.t2, marginBottom: 4, marginTop: 8, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 };
  var inp = { width: "100%", background: C.bg, color: C.t1, border: "1px solid " + C.bd, borderRadius: 6, padding: "6px 8px", fontSize: 13, boxSizing: "border-box" };

  return (
    <div>
      {/* Top bar: name + saved designs */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 14 }}>
        <input value={cur.name} onChange={function(e) { setCur(Object.assign({}, cur, { name: e.target.value })); }}
          placeholder={t(lang, "qslDesignName")} style={Object.assign({}, inp, { width: 200, fontWeight: 700 })} />
        <button style={btnPrimary} disabled={saving} onClick={function() { saveDesign(false); }}>{t(lang, "qslDesignSave")}</button>
        <button style={btn} disabled={saving} onClick={function() { saveDesign(true); }}>{t(lang, "qslDesignSaveAsNew")}</button>
        <button style={btn} onClick={setDefault}>{cur.is_default ? "\u2605 " + t(lang, "qslDesignIsDefault") : t(lang, "qslDesignSetDefault")}</button>
        {cur.id ? <button style={Object.assign({}, btn, { color: C.a5 })} onClick={deleteDesign}>{t(lang, "qslDesignDelete")}</button> : null}
        {msg ? <span style={{ color: msg.ok ? C.a2 : C.a5, fontSize: 13, fontWeight: 600 }}>{msg.txt}</span> : null}
      </div>

      {/* Saved designs strip */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14, alignItems: "center" }}>
        <span style={{ fontSize: 12, color: C.t2 }}>{t(lang, "qslDesignPresets")}:</span>
        {presets.map(function(p, i) {
          return <button key={"p" + i} style={Object.assign({}, btn, { padding: "6px 10px", fontSize: 12 })} onClick={function() { newFromPreset(p); }}>+ {p.name}</button>;
        })}
        {designs.length ? <span style={{ fontSize: 12, color: C.t2, marginLeft: 8 }}>{t(lang, "qslDesignSaved")}:</span> : null}
        {designs.map(function(d) {
          return <button key={d.id} style={Object.assign({}, btn, { padding: "6px 10px", fontSize: 12, borderColor: cur.id === d.id ? C.a1 : C.bd })} onClick={function() { loadDesign(d); }}>
            {d.is_default ? "\u2605 " : ""}{d.name}
          </button>;
        })}
      </div>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
        {/* Left toolbar */}
        <div style={{ width: 150, flexShrink: 0 }}>
          <div style={lbl}>{t(lang, "qslDesignAdd")}</div>
          <button style={Object.assign({}, btn, { width: "100%", marginBottom: 6 })} onClick={function() { addElement("text"); }}>+ {t(lang, "qslDesignText")}</button>
          <select style={Object.assign({}, inp, { marginBottom: 6 })} value="" onChange={function(e) { if (e.target.value) { addElement("field", e.target.value); e.target.value = ""; } }}>
            <option value="">+ {t(lang, "qslDesignField")}</option>
            {FIELD_DEFS.map(function(f) { return <option key={f.key} value={f.key}>{f.label}</option>; })}
          </select>
          <button style={Object.assign({}, btn, { width: "100%", marginBottom: 6 })} onClick={function() { addElement("rect"); }}>+ {t(lang, "qslDesignBox")}</button>
          <button style={Object.assign({}, btn, { width: "100%", marginBottom: 6 })} onClick={function() { addElement("line"); }}>+ {t(lang, "qslDesignLine")}</button>
          <label style={Object.assign({}, btn, { width: "100%", marginBottom: 6, display: "block", textAlign: "center", boxSizing: "border-box" })}>
            + {t(lang, "qslDesignLogo")}
            <input type="file" accept="image/*" style={{ display: "none" }} onChange={onLogoFile} />
          </label>

          <div style={lbl}>{t(lang, "qslDesignBg")}</div>
          <select style={Object.assign({}, inp, { marginBottom: 6 })} value={layout.bg.type}
            onChange={function(e) {
              var ty = e.target.value;
              var bg = ty === "solid" ? { type: "solid", color: "#0c1133" }
                : ty === "linear" ? { type: "linear", c1: "#0c1133", c2: "#1b2356", angle: 35 }
                : { type: "image", src: (layout.bg.src || ""), overlay: "rgba(12,17,51,0.35)" };
              setLayout(Object.assign({}, layout, { bg: bg }));
            }}>
            <option value="solid">{t(lang, "qslDesignBgSolid")}</option>
            <option value="linear">{t(lang, "qslDesignBgGradient")}</option>
            <option value="image">{t(lang, "qslDesignBgImage")}</option>
          </select>
          {layout.bg.type === "solid" ? <input type="color" value={layout.bg.color || "#0c1133"} onChange={function(e) { setLayout(Object.assign({}, layout, { bg: Object.assign({}, layout.bg, { color: e.target.value }) })); }} style={{ width: "100%", height: 32 }} /> : null}
          {layout.bg.type === "linear" ? <div style={{ display: "flex", gap: 6 }}>
            <input type="color" value={layout.bg.c1 || "#0c1133"} onChange={function(e) { setLayout(Object.assign({}, layout, { bg: Object.assign({}, layout.bg, { c1: e.target.value }) })); }} style={{ flex: 1, height: 32 }} />
            <input type="color" value={layout.bg.c2 || "#1b2356"} onChange={function(e) { setLayout(Object.assign({}, layout, { bg: Object.assign({}, layout.bg, { c2: e.target.value }) })); }} style={{ flex: 1, height: 32 }} />
          </div> : null}
          {layout.bg.type === "image" ? <div>
            {layout.bg.src ? <img src={layout.bg.src} alt="" style={{ width: "100%", height: 56, objectFit: "cover", borderRadius: 6, border: "1px solid " + C.bd, marginBottom: 6, display: "block" }} /> : null}
            <label style={Object.assign({}, btn, { width: "100%", display: "block", textAlign: "center", boxSizing: "border-box", marginBottom: 6 })}>
              {layout.bg.src ? t(lang, "qslDesignBgChange") : t(lang, "qslDesignUpload")}
              <input type="file" accept="image/*" style={{ display: "none" }} onChange={onBgFile} />
            </label>
            {layout.bg.src ? <div>
              <label style={lbl}>{t(lang, "qslDesignBgDarken")}: {Math.round(bgOverlayAlpha(layout.bg.overlay) * 100)}%</label>
              <input type="range" min="0" max="90" value={Math.round(bgOverlayAlpha(layout.bg.overlay) * 100)}
                onChange={function(e) { var a = parseInt(e.target.value, 10) / 100; setLayout(Object.assign({}, layout, { bg: Object.assign({}, layout.bg, { overlay: "rgba(8,11,30," + a + ")" }) })); }}
                style={{ width: "100%" }} />
              <button style={Object.assign({}, btn, { width: "100%", marginTop: 6, color: C.a5 })}
                onClick={function() { setLayout(Object.assign({}, layout, { bg: { type: "linear", c1: "#0c1133", c2: "#1b2356", angle: 35 } })); }}>
                {t(lang, "qslDesignBgRemove")}
              </button>
            </div> : null}
          </div> : null}
        </div>

        {/* Canvas */}
        <div style={{ flex: 1, minWidth: 320, maxWidth: 760 }}>
          <canvas ref={canvasRef} width={CARD_W} height={CARD_H}
            onMouseDown={onCanvasDown}
            style={{ width: "100%", borderRadius: 10, border: "1px solid " + C.bd, cursor: "move", display: "block", background: "#000" }} />
          <div style={{ fontSize: 11, color: C.t3, marginTop: 6 }}>{t(lang, "qslDesignHint")}</div>
        </div>

        {/* Properties panel */}
        <div style={{ width: 220, flexShrink: 0, background: C.s1, border: "1px solid " + C.bd, borderRadius: 10, padding: 12, boxSizing: "border-box" }}>
          {!sel ? <div style={{ color: C.t3, fontSize: 13 }}>{t(lang, "qslDesignSelectHint")}</div> : (
            <div>
              <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                <button style={Object.assign({}, btn, { flex: 1, padding: "5px", fontSize: 11 })} onClick={duplicateSel}>{t(lang, "qslDesignDuplicate")}</button>
                <button style={Object.assign({}, btn, { flex: 1, padding: "5px", fontSize: 11, color: C.a5 })} onClick={deleteSel}>{t(lang, "qslDesignDelete")}</button>
              </div>
              <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                <button style={Object.assign({}, btn, { flex: 1, padding: "5px", fontSize: 11 })} onClick={function() { reorderSel("front"); }}>{t(lang, "qslDesignFront")}</button>
                <button style={Object.assign({}, btn, { flex: 1, padding: "5px", fontSize: 11 })} onClick={function() { reorderSel("back"); }}>{t(lang, "qslDesignBack")}</button>
              </div>

              {(sel.type === "text" || sel.type === "field") ? (
                <div>
                  {sel.type === "text" ?
                    <div><label style={lbl}>{t(lang, "qslDesignTextContent")}</label>
                      <input style={inp} value={sel.text || ""} onChange={function(e) { updateSel({ text: e.target.value }); }} /></div>
                    :
                    <div><label style={lbl}>{t(lang, "qslDesignField")}</label>
                      <select style={inp} value={sel.field} onChange={function(e) { updateSel({ field: e.target.value }); }}>
                        {FIELD_DEFS.map(function(f) { return <option key={f.key} value={f.key}>{f.label}</option>; })}
                      </select></div>
                  }
                  <label style={lbl}>{t(lang, "qslDesignCaption")}</label>
                  <input style={inp} value={sel.label || ""} placeholder="—" onChange={function(e) { updateSel({ label: e.target.value }); }} />
                  <label style={lbl}>{t(lang, "qslDesignSize")}: {sel.size}px</label>
                  <input type="range" min="8" max="90" value={sel.size || 20} onChange={function(e) { updateSel({ size: parseInt(e.target.value, 10) }); }} style={{ width: "100%" }} />
                  <label style={lbl}>{t(lang, "qslDesignColor")}</label>
                  <input type="color" value={sel.color || "#ffffff"} onChange={function(e) { updateSel({ color: e.target.value }); }} style={{ width: "100%", height: 30 }} />
                  <label style={lbl}>{t(lang, "qslDesignAlign")}</label>
                  <div style={{ display: "flex", gap: 4 }}>
                    {["left", "center", "right"].map(function(a) {
                      return <button key={a} onClick={function() { updateSel({ align: a }); }}
                        style={Object.assign({}, btn, { flex: 1, padding: "5px", fontSize: 11, background: (sel.align || "left") === a ? C.a1 : C.s2, color: (sel.align || "left") === a ? "#04223a" : C.t1 })}>{a[0].toUpperCase()}</button>;
                    })}
                  </div>
                  <label style={lbl}>{t(lang, "qslDesignStyle")}</label>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button onClick={function() { updateSel({ weight: sel.weight === "bold" ? "normal" : "bold" }); }}
                      style={Object.assign({}, btn, { flex: 1, padding: "5px", fontWeight: 800, background: sel.weight === "bold" ? C.a1 : C.s2, color: sel.weight === "bold" ? "#04223a" : C.t1 })}>B</button>
                    <button onClick={function() { updateSel({ italic: !sel.italic }); }}
                      style={Object.assign({}, btn, { flex: 1, padding: "5px", fontStyle: "italic", background: sel.italic ? C.a1 : C.s2, color: sel.italic ? "#04223a" : C.t1 })}>I</button>
                  </div>
                  <label style={lbl}>{t(lang, "qslDesignFont")}</label>
                  <select style={inp} value={sel.font || "Arial"} onChange={function(e) { updateSel({ font: e.target.value }); }}>
                    <option value="Arial">Arial</option>
                    <option value="'Courier New'">Courier</option>
                    <option value="Georgia">Georgia</option>
                    <option value="'Trebuchet MS'">Trebuchet</option>
                    <option value="Impact">Impact</option>
                  </select>
                  <label style={lbl}>{t(lang, "qslDesignGlow")}</label>
                  <input type="range" min="0" max="30" value={sel.shadow || 0} onChange={function(e) { var s = parseInt(e.target.value, 10); updateSel({ shadow: s, shadowColor: sel.shadowColor || sel.color }); }} style={{ width: "100%" }} />
                </div>
              ) : null}

              {sel.type === "rect" ? (
                <div>
                  <label style={lbl}>{t(lang, "qslDesignWidth")}: {sel.w}</label>
                  <input type="range" min="10" max="900" value={sel.w} onChange={function(e) { updateSel({ w: parseInt(e.target.value, 10) }); }} style={{ width: "100%" }} />
                  <label style={lbl}>{t(lang, "qslDesignHeight")}: {sel.h}</label>
                  <input type="range" min="10" max="540" value={sel.h} onChange={function(e) { updateSel({ h: parseInt(e.target.value, 10) }); }} style={{ width: "100%" }} />
                  <label style={lbl}>{t(lang, "qslDesignFill")}</label>
                  <input type="color" value={(sel.fill || "#000000").substring(0, 7)} onChange={function(e) { updateSel({ fill: e.target.value }); }} style={{ width: "100%", height: 30 }} />
                  <label style={lbl}>{t(lang, "qslDesignBorder")}</label>
                  <input type="color" value={(sel.stroke || "#34407e").substring(0, 7)} onChange={function(e) { updateSel({ stroke: e.target.value }); }} style={{ width: "100%", height: 30 }} />
                  <label style={lbl}>{t(lang, "qslDesignRadius")}: {sel.radius || 0}</label>
                  <input type="range" min="0" max="60" value={sel.radius || 0} onChange={function(e) { updateSel({ radius: parseInt(e.target.value, 10) }); }} style={{ width: "100%" }} />
                </div>
              ) : null}

              {sel.type === "image" ? (
                <div>
                  <label style={lbl}>{t(lang, "qslDesignWidth")}: {sel.w}</label>
                  <input type="range" min="20" max="900" value={sel.w} onChange={function(e) { updateSel({ w: parseInt(e.target.value, 10) }); }} style={{ width: "100%" }} />
                  <label style={lbl}>{t(lang, "qslDesignHeight")}: {sel.h}</label>
                  <input type="range" min="20" max="540" value={sel.h} onChange={function(e) { updateSel({ h: parseInt(e.target.value, 10) }); }} style={{ width: "100%" }} />
                  <label style={lbl}>{t(lang, "qslDesignRadius")}: {sel.radius || 0}</label>
                  <input type="range" min="0" max="120" value={sel.radius || 0} onChange={function(e) { updateSel({ radius: parseInt(e.target.value, 10) }); }} style={{ width: "100%" }} />
                </div>
              ) : null}

              {sel.type === "line" ? (
                <div>
                  <label style={lbl}>{t(lang, "qslDesignColor")}</label>
                  <input type="color" value={sel.color || "#3fcfd5"} onChange={function(e) { updateSel({ color: e.target.value }); }} style={{ width: "100%", height: 30 }} />
                  <label style={lbl}>{t(lang, "qslDesignThickness")}: {sel.sw || 2}</label>
                  <input type="range" min="1" max="20" value={sel.sw || 2} onChange={function(e) { updateSel({ sw: parseInt(e.target.value, 10) }); }} style={{ width: "100%" }} />
                </div>
              ) : null}

              <label style={lbl}>{t(lang, "qslDesignOpacity")}: {Math.round((sel.opacity != null ? sel.opacity : 1) * 100)}%</label>
              <input type="range" min="0" max="100" value={Math.round((sel.opacity != null ? sel.opacity : 1) * 100)} onChange={function(e) { updateSel({ opacity: parseInt(e.target.value, 10) / 100 }); }} style={{ width: "100%" }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
